import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useMymTitlesConfig, MYM_GAME_LABELS, MYM_TITLE_CATEGORIES } from "./useMymTitlesConfig";
import ForceInviteConfigSection from "./ForceInviteConfigSection";
import ForceAutoOffSection from "./ForceAutoOffSection";

const MymTitlesConfigPanel = ({
  showSplashEdit = true,
  compact = false,
  showForceSections = true,
}) => {
  const {
    mymConfig,
    mymLoading,
    mymSaving,
    forceWarningOpen,
    forceWarningMessage,
    forceExclusiveOpen,
    splashEditOpen,
    splashEditItem,
    splashTitle,
    splashDescription,
    setForceWarningOpen,
    setSplashTitle,
    setSplashDescription,
    handleMymToggle,
    handleGoLiveStop,
    handleCategoryBulk,
    confirmForceExclusive,
    cancelForceExclusive,
    openSplashEdit,
    closeSplashEdit,
    handleSplashEditSave,
  } = useMymTitlesConfig();

  if (mymLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#1DB954" }} />
      </Box>
    );
  }

  return (
    <>
      {MYM_TITLE_CATEGORIES.map((category) => (
        <Box key={category.id} sx={{ mb: compact ? 2 : 2.5 }}>
          {(() => {
            const items = category.ids
              .map((id) => mymConfig.find((entry) => entry.id === id))
              .filter(Boolean);
            const allShown = items.length > 0 && items.every((item) => !item.hidden);
            const allOnAir = items.length > 0 && items.every((item) => !!item.enabled);
            const bulkButtonSx = (active) => ({
              color: "#fff",
              fontWeight: 700,
              fontSize: compact ? "0.7rem" : "0.8125rem",
              backgroundColor: active ? "#1DB850" : "#e53935",
              "&:hover": {
                backgroundColor: active ? "#17a348" : "#c62828",
              },
            });
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#00FFFF",
                    fontWeight: 700,
                    fontSize: compact ? "0.72rem" : "0.8rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  {category.label}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={mymSaving}
                    onClick={() => handleCategoryBulk(category.ids, "hidden", !allShown)}
                    sx={bulkButtonSx(!allShown)}
                  >
                    {allShown ? "Stop show" : "Show all"}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={mymSaving}
                    onClick={() => handleCategoryBulk(category.ids, "enabled", !allOnAir)}
                    sx={bulkButtonSx(!allOnAir)}
                  >
                    {allOnAir ? "Stop on air" : "On Air all"}
                  </Button>
                </Box>
              </Box>
            );
          })()}
          <Paper
            elevation={0}
            sx={{
              p: compact ? 1.5 : 2,
              backgroundColor: "#1A1A1A",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "12px",
            }}
          >
            {category.ids.map((id) => {
              const item = mymConfig.find((entry) => entry.id === id);
              if (!item) return null;
              const isLive = !item.hidden && !!item.enabled;
              return (
                <Box
                  key={item.id}
                  sx={{
                    py: compact ? 1.25 : 1.5,
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      mb: 1,
                      fontSize: compact ? "0.85rem" : "0.95rem",
                    }}
                  >
                    {MYM_GAME_LABELS[item.id] || item.id}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: compact ? 1.5 : 2,
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!item.hidden}
                          onChange={(e) =>
                            handleMymToggle(item.id, "hidden", !e.target.checked)
                          }
                          disabled={mymSaving}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#1DB954" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#1DB954",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: "#aaa", fontSize: compact ? "0.75rem" : "0.875rem" }}>
                          Show tile
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!item.enabled}
                          onChange={(e) =>
                            handleMymToggle(item.id, "enabled", e.target.checked)
                          }
                          disabled={mymSaving}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#1DB954" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#1DB954",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: "#aaa", fontSize: compact ? "0.75rem" : "0.875rem" }}>
                          On Air
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!item.forceModal}
                          onChange={(e) =>
                            handleMymToggle(item.id, "forceModal", e.target.checked)
                          }
                          disabled={mymSaving}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff4081" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#ff4081",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: "#aaa", fontSize: compact ? "0.75rem" : "0.875rem" }}>
                          Force
                        </Typography>
                      }
                    />
                    {showSplashEdit && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openSplashEdit(item)}
                        sx={{
                          color: "#00FFFF",
                          borderColor: "#00FFFF",
                          fontSize: compact ? "0.7rem" : "0.8125rem",
                          py: compact ? 0.25 : 0.5,
                          "&:hover": { borderColor: "#00FFFF", bgcolor: "rgba(0,255,255,0.08)" },
                        }}
                      >
                        Edit splash
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      disabled={mymSaving}
                      onClick={() => handleGoLiveStop(item.id)}
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: compact ? "0.7rem" : "0.8125rem",
                        backgroundColor: isLive ? "#e53935" : "#1DB850",
                        "&:hover": {
                          backgroundColor: isLive ? "#c62828" : "#17a348",
                        },
                      }}
                    >
                      {isLive ? "Stop" : "Go Live"}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
      ))}

      {showForceSections && (
        <>
          <ForceInviteConfigSection compact />
          <ForceAutoOffSection compact />
        </>
      )}

      {showSplashEdit && (
        <Dialog
          open={splashEditOpen}
          onClose={closeSplashEdit}
          PaperProps={{
            sx: {
              backgroundColor: "#1E1E1E",
              border: "1px solid #333",
              borderRadius: "12px",
              minWidth: 360,
            },
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>
            Edit splash — {splashEditItem ? MYM_GAME_LABELS[splashEditItem.id] : ""}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Splash title"
              value={splashTitle}
              onChange={(e) => setSplashTitle(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
              InputProps={{ sx: { color: "#fff" } }}
              InputLabelProps={{ sx: { color: "#aaa" } }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Splash description"
              value={splashDescription}
              onChange={(e) => setSplashDescription(e.target.value)}
              InputProps={{ sx: { color: "#fff" } }}
              InputLabelProps={{ sx: { color: "#aaa" } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={closeSplashEdit} sx={{ color: "#aaa" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSplashEditSave}
              sx={{
                background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
                color: "#fff",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={forceWarningOpen}
        onClose={() => setForceWarningOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1E1E1E",
            border: "1px solid #333",
            borderRadius: "12px",
            minWidth: 320,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Force modal rule</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#ddd" }}>{forceWarningMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setForceWarningOpen(false)} sx={{ color: "#fff" }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forceExclusiveOpen}
        onClose={cancelForceExclusive}
        PaperProps={{
          sx: {
            backgroundColor: "#1E1E1E",
            border: "1px solid #333",
            borderRadius: "12px",
            minWidth: 320,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Switch forced card?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#ddd" }}>
            Another card is already forced. Do you want to turn off force on that
            card and force this one instead?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={cancelForceExclusive} sx={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button onClick={confirmForceExclusive} sx={{ color: "#fff" }} variant="contained">
            Yes, switch
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MymTitlesConfigPanel;
